import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { notFound } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import React from "react";

import { EmptyState } from "~/components/ui/empty-state";
import { ResourceCard } from "~/components/ui/resource-card";
import { coachQueryOptions } from "~/lib/query-options";
import { createMetaTags } from "~/lib/shared/meta";

export const Route = createFileRoute("/$username/_profile/resources/")({
  loader: async ({ context, params }) => {
    // Use the same query options as the main profile
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );

    if (!coach || !coach.resources || coach.resources.length === 0) {
      throw notFound();
    }

    return { coach };
  },
  head: ({ loaderData, params }) => {
    const coachName = loaderData?.coach?.fullName ?? params.username;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/resources`;

    return createMetaTags({
      title: `Resources & Articles by ${coachName} | Dream Coach Match`,
      description: `Free resources, guides, and articles by ${coachName} on Dream Coach Match.`,
      ogUrl: currentUrl,
      canonical: currentUrl,
    });
  },
  component: ResourcesPage,
});

function ResourcesPage() {
  const { username } = Route.useParams();
  const { coach } = Route.useLoaderData();

  const { data: queryCoach } = useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  if (!queryCoach?.profile) {
    return "Coach not found.";
  }

  const coachName = queryCoach.fullName ?? username;

  return (
    <>
      {/* The listing had no <h1> at all: the coach chrome supplies none and only
          the resource detail page carries one, so this page shipped headingless
          to crawlers. */}
      <h1 className="text-text-primary mb-8 font-heading text-4xl font-medium">
        Resources & Articles by {coachName}
      </h1>
      {queryCoach.resources.length ? (
        <div className="grid gap-8 md:grid-cols-2">
          {queryCoach.resources.map((resource) => (
            <ResourceCard
              className="h-full"
              key={resource.id}
              resource={resource}
              coachUsername={queryCoach.username}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          size="lg"
          icon={BookOpen}
          title="No resources yet"
          description={`${queryCoach.fullName ?? "This coach"} hasn't shared any resources or articles yet. Check back soon.`}
        />
      )}
    </>
  );
}
